import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconX, IconCopy, IconCheck, IconClock,IconFlame } from '@tabler/icons-react';
import { thunkFetchExpiringCouponsForClient } from'../../../redux/slices/couponSlice';
import CountdownTimer from './CountdownTimer';

const ExpiringCouponsBanner = () => {
  const dispatch = useDispatch();
  const [currentCouponIndex, setCurrentCouponIndex] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [hasFetchedCoupons, setHasFetchedCoupons] = useState(false);

  const { 
    clientExpiringCoupons, 
    clientLoading, 
    clientError 
  } = useSelector((state) => state.coupon);

  // Check if banner was dismissed in this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem('expiringCouponsBannerDismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  // Fetch expiring coupons when component mounts
  useEffect(() => {
    if (!hasFetchedCoupons) {
      dispatch(thunkFetchExpiringCouponsForClient())
        .finally(() => setHasFetchedCoupons(true));
    }
  }, [dispatch, hasFetchedCoupons]);

  // Auto-rotate coupons every 8 seconds
  useEffect(() => {
    if (clientExpiringCoupons && clientExpiringCoupons.length > 1) {
      const interval = setInterval(() => {
        setCurrentCouponIndex((prevIndex) => 
          (prevIndex + 1) % clientExpiringCoupons.length
        );
      }, 8000);

      return () => clearInterval(interval);
    }
  }, [clientExpiringCoupons]);

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('expiringCouponsBannerDismissed', 'true');
  };

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const handleCouponExpired = () => {
    // Remove expired coupon and move to next
    if (clientExpiringCoupons.length > 1) {
      setCurrentCouponIndex((prevIndex) => 
        prevIndex >= clientExpiringCoupons.length - 1 ? 0 : prevIndex
      );
    } else {
      // If this was the last coupon, dismiss banner
      handleDismiss();
    }
  };

  // Don't render if dismissed, loading, or no coupons
  if (isDismissed || clientLoading || !clientExpiringCoupons || clientExpiringCoupons.length === 0) {
    return null;
  }

  const currentCoupon = clientExpiringCoupons[currentCouponIndex];
  if (!currentCoupon) return null;

  const formatDiscount = (coupon) => {
    if (coupon.coupon_type === 'percentage') {
      return `${coupon.discount_value}% OFF`;
    } else if (coupon.coupon_type === 'fixed_amount') {
      return `$${coupon.discount_value} OFF`;
    } else if (coupon.coupon_type === 'free_shipping') {
      return 'FREE SHIPPING';
    }
    return 'SPECIAL OFFER';
  };

  const getUrgencyStyles = () => {
    const timeRemaining = new Date(currentCoupon.end_date) - new Date();
    const hoursLeft = timeRemaining / (1000 * 60 * 60);

    if (hoursLeft <= 6) {
      return 'bg-gradient-to-r from-red-600 to-red-500 animate-pulse';
    } else if (hoursLeft <= 24) {
      return 'bg-gradient-to-r from-orange-600 to-orange-500';
    } else {
      return 'bg-gradient-to-r from-purple-600 to-purple-500';
    }
  };

  return (
    <div className={`${getUrgencyStyles()} text-white py-3 px-4 relative z-30`}>
      <div className="max-w-screen-xl mx-auto flex items-center justify-between">
        {/* Left side - Urgency indicator and message */}
        <div className="flex items-center gap-3">
          <IconFlame size={24} className="animate-bounce" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <span className="font-bold text-lg">
              {formatDiscount(currentCoupon)}
            </span>
            <span className="text-sm opacity-90">
              {currentCoupon.name} - Limited Time!
            </span>
          </div>
        </div>

        {/* Center - Countdown Timer */}
        <div className="flex items-center gap-2">
          <IconClock size={20} />
          <CountdownTimer 
            endDate={currentCoupon.end_date}
            onExpired={handleCouponExpired}
            className="text-lg"
          />
        </div>

        {/* Right side - Code and actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleCopyCode(currentCoupon.code)}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 
                       px-4 py-2 rounded-lg transition-all font-bold text-sm"
          >
            {copiedCode === currentCoupon.code ? (
              <>
                <IconCheck size={16} />
                Copied!
              </>
            ) : (
              <>
                <IconCopy size={16} />
                {currentCoupon.code}
              </>
            )}
          </button>

          {/* Coupon indicators */}
          {clientExpiringCoupons.length > 1 && (
            <div className="hidden sm:flex items-center gap-1">
              {clientExpiringCoupons.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentCouponIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentCouponIndex 
                      ? 'bg-white' 
                      : 'bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
          )}

          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-white/20 rounded transition-colors"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      {/* Mobile-friendly bottom text */}
      <div className="sm:hidden mt-2 text-center text-xs opacity-90">
        Tap code to copy • {clientExpiringCoupons.length} deals expiring soon
      </div>
    </div>
  );
};

export default ExpiringCouponsBanner;